import { MapData_Base, MapData_ProcessStatus, MapData_ModelUtil } from '~/common/typescript/models/MapData';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Tag, Button, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { memo } from 'react';
import MapDataThumbnail from '~/components/app_common/MapDataThumbnail/MapDataThumbnail';

interface LibraryItemProps {
  item: MapData_Base;
  onAdd: (item: MapData_Base) => void;
}

const LibraryItem = ({ item, onAdd }: LibraryItemProps) => {
  const { t } = useTranslation();

  const getDataType = () => {
    try {
      if (item.metadata) {
        const parsed = JSON.parse(item.metadata);
        if (parsed.isVector) return 'Vector';
        if (parsed.isRaster) return 'Raster';
      }
    } catch {
      // ignore
    }
    return 'Unknown';
  };

  const getGeometryType = () => {
    try {
      if (item.metadata) {
        const parsed = JSON.parse(item.metadata);
        if (parsed.driverShortName) {
          const driver = parsed.driverShortName.toLowerCase();
          if (driver.includes('point')) return 'Point';
          if (driver.includes('line') || driver.includes('polyline')) return 'Line';
          if (driver.includes('polygon')) return 'Polygon';
          if (driver.includes('raster')) return 'Raster';
        }
      }
    } catch {
      // ignore
    }
    return null;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const dataType = getDataType();
  const geometryType = getGeometryType();

  return (
    <Card>
      <ImageWrapper>
        <MapDataThumbnail 
          documentId={item.documentId || ''} 
          mapDataId={item.id || 0} 
          size="medium"
          editable={false}
        />
        <StatusBadge color={MapData_ModelUtil.getStatusColor(item.processStatus || MapData_ProcessStatus.PENDING)}>
          {MapData_ModelUtil.getTextStatus(item.processStatus || MapData_ProcessStatus.PENDING)}
        </StatusBadge>
      </ImageWrapper>

      <Content>
        <Tooltip title={item.name || t('untitled')} placement="topLeft">
          <Title>{item.name || t('untitled')}</Title>
        </Tooltip>
        
        <TagsRow>
          {dataType !== 'Unknown' && <Tag color="blue">{dataType}</Tag>}
          {geometryType && <Tag color="green">{geometryType}</Tag>}
        </TagsRow>

        <MetaRow>
          <MetaItem>
            <MetaLabel>{t('owner')}</MetaLabel>
            <MetaValue>{item.owner?.username || '-'}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t('date')}</MetaLabel>
            <MetaValue>{formatDate(item.createdAt)}</MetaValue>
          </MetaItem>
        </MetaRow>

        {item.description && (
          <Description>{item.description}</Description>
        )}

        <AddButton type="primary" icon={<PlusOutlined />} onClick={() => onAdd(item)}>
          {t('add')}
        </AddButton>
      </Content>
    </Card>
  );
};

export default memo(LibraryItem);

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 120px;
  background: #f5f7fa;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusBadge = styled.div<{ color: string }>`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${props => props.color};
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  z-index: 10;
`;

const Content = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  color: #bbb;
`;

const MetaValue = styled.span`
  color: #666;
`;

const Description = styled.div`
  font-size: 12px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 32px;
`;

const AddButton = styled(Button)`
  width: 100%;
  margin-top: auto;
`;
